import { useCallback, useEffect, useMemo, useState } from "react";

import { getGatewayBaseUrl } from "../runtime/config";
import type { EmployeeRuntimeActor } from "../runtime/gateway/identity-client";
import {
  PosReceivingGatewayClient,
  type ReceivingCloseReason,
  type ReceivingQueue,
  type ReceivingQueueReceipt,
} from "../runtime/gateway/receiving-client";
import { readLocalEmployeeSession } from "../runtime/local-store/employee-session";
import { readReceivingQueue, saveReceivingQueue } from "../runtime/local-store/secondary-runtime-cache";
import type { TerminalCredentialState } from "../runtime/local-store/terminal-state";
import "./ReceivingPanel.css";

type Props = {
  actor: EmployeeRuntimeActor;
  terminal: TerminalCredentialState;
  online: boolean;
  disabled?: boolean;
};

type Mode = "LIST" | "RECEIVE" | "RECEIVE_REVIEW" | "CLOSE" | "CLOSE_REVIEW";
type ReceivingView = "OPEN" | "RESOLVED";

function requestKey(prefix: string) {
  const id = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${id}`;
}

function displayError(value: string) {
  if (value.includes("GATEWAY_UNREACHABLE")) return "ติดต่อ MORE POS Gateway ไม่ได้";
  if (value.includes("GATEWAY_TIMEOUT")) return "Gateway ตอบกลับช้าเกินไป กรุณาลองอีกครั้งด้วยคำขอเดิม";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "Session พนักงานใช้ไม่ได้แล้ว กรุณา Login ใหม่";
  if (value.includes("POS_STOCK_RECEIVING_PERMISSION_REQUIRED")) return "พนักงานคนนี้ไม่มีสิทธิ์รับสินค้า";
  if (value.includes("PURCHASE_RECEIVING_NOT_ENABLED_FOR_BRANCH")) return "สาขานี้ยังไม่ได้เปิดระบบรับสินค้าจากรายการซื้อ";
  if (value.includes("PURCHASE_RECEIVING_LINE_NEEDS_SETUP")) return "มีรายการที่ต้องตั้งค่าหน่วยหรือ Master ก่อนรับสินค้า";
  if (value.includes("PURCHASE_RECEIVING_STATE_CHANGED")) return "สถานะบิลเปลี่ยนแล้ว กรุณารีเฟรชก่อนทำรายการต่อ";
  if (value.includes("PURCHASE_RECEIVING_CLOSE_NOTE_REQUIRED")) return "เหตุผลนี้ต้องระบุหมายเหตุ";
  if (value.includes("PURCHASE_RECEIVING_REQUEST_KEY_CONFLICT")) return "คำขอนี้ขัดกับข้อมูลเดิม กรุณารีเฟรช";
  return "ทำรายการรับสินค้าไม่สำเร็จ กรุณารีเฟรชแล้วตรวจสอบอีกครั้ง";
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "numeric" });
}

function asNumber(value: string) {
  const quantity = Number(value);
  return Number.isFinite(quantity) ? quantity : 0;
}

function isResolved(receipt: ReceivingQueueReceipt) {
  return receipt.receiving_status === "RECEIVED" || receipt.receiving_status === "CLOSED_WITH_SHORTAGE";
}

function receiptStatusLabel(receipt: ReceivingQueueReceipt) {
  if (receipt.receiving_status === "PENDING") return "รอรับสินค้า";
  if (receipt.receiving_status === "PARTIAL") return "รับแล้วบางส่วน";
  if (receipt.receiving_status === "NEEDS_SETUP") return "ต้องตั้งค่าก่อน";
  if (receipt.receiving_status === "RECEIVED") return "รับครบแล้ว";
  if (receipt.receiving_status === "CLOSED_WITH_SHORTAGE") return "ปิดยอดค้างแล้ว";
  return receipt.receiving_status;
}

function sortReceipts(receipts: ReceivingQueueReceipt[], view: ReceivingView) {
  return [...receipts].sort((left, right) => {
    if (view === "RESOLVED") {
      const eventCompare = (right.last_event_at ?? "").localeCompare(left.last_event_at ?? "");
      if (eventCompare !== 0) return eventCompare;
    }
    const dateCompare = (right.purchase_date ?? "").localeCompare(left.purchase_date ?? "");
    if (dateCompare !== 0) return dateCompare;
    const createdCompare = (right.receipt_created_at ?? "").localeCompare(left.receipt_created_at ?? "");
    if (createdCompare !== 0) return createdCompare;
    return right.receipt_id.localeCompare(left.receipt_id);
  });
}

export function ReceivingPanel({ actor, terminal, online, disabled = false }: Props) {
  const canReceive = actor.permission_codes.includes("stock.receive.purchase");
  const identity = terminal.identity;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ReceivingView>("OPEN");
  const [queue, setQueue] = useState<ReceivingQueue | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedReceiptId, setSelectedReceiptId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("LIST");
  const [quantityDraft, setQuantityDraft] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [reasons, setReasons] = useState<ReceivingCloseReason[]>([]);
  const [reasonCode, setReasonCode] = useState("");
  const [pendingRequestKey, setPendingRequestKey] = useState<string | null>(null);

  const selectedReceipt = useMemo(
    () => queue?.receipts.find((receipt) => receipt.receipt_id === selectedReceiptId) ?? null,
    [queue, selectedReceiptId],
  );

  const pendingCount =
    (queue?.summary.pending_receipt_count ?? 0) +
    (queue?.summary.partial_receipt_count ?? 0) +
    (queue?.summary.needs_setup_receipt_count ?? 0);

  const resolvedCount =
    (queue?.summary.received_receipt_count ?? 0) +
    (queue?.summary.closed_with_shortage_receipt_count ?? 0);

  const visibleReceipts = useMemo(
    () => sortReceipts(
      (queue?.receipts ?? []).filter((receipt) => view === "RESOLVED" ? isResolved(receipt) : !isResolved(receipt)),
      view,
    ),
    [queue, view],
  );

  const withClient = useCallback(async <T,>(run: (
    client: PosReceivingGatewayClient,
    auth: { deviceSecret: string; sessionToken: string },
  ) => Promise<T>): Promise<T> => {
    const baseUrl = getGatewayBaseUrl();
    const localSession = await readLocalEmployeeSession();
    const deviceSecret = terminal.deviceSecret;
    if (!baseUrl || !deviceSecret || !localSession?.sessionToken) throw new Error("POS_RECEIVING_RUNTIME_NOT_READY");
    if (localSession.actor.runtime_session_id !== actor.runtime_session_id) throw new Error("POS_EMPLOYEE_RUNTIME_SESSION_INVALID");
    const client = new PosReceivingGatewayClient({ baseUrl });
    return run(client, { deviceSecret, sessionToken: localSession.sessionToken });
  }, [actor.runtime_session_id, terminal.deviceSecret]);

  const loadQueue = useCallback(async () => {
    if (!canReceive || !identity) return;
    setLoading(true);
    setError(null);
    try {
      const local = await readReceivingQueue(identity);
      if (local) setQueue(local);
      if (!online || !terminal.deviceSecret) return;

      const fullReceipts = new Map<string, ReceivingQueueReceipt>();
      let firstPage: ReceivingQueue | null = null;
      let offset = 0;
      const visitedOffsets = new Set<number>();

      while (!visitedOffsets.has(offset)) {
        visitedOffsets.add(offset);
        const result = await withClient((client, auth) => client.queue({
          ...auth,
          includeResolved: true,
          limit: 50,
          offset,
        }));
        if (!result.ok || !result.data) throw new Error(result.error ?? "POS_STOCK_RECEIVING_FAILED");
        firstPage ??= result.data;
        for (const receipt of result.data.receipts) {
          fullReceipts.set(receipt.receipt_id, receipt);
        }
        if (!result.data.pagination.has_more || result.data.receipts.length === 0) break;
        offset += result.data.receipts.length;
      }

      if (!firstPage) throw new Error("POS_STOCK_RECEIVING_FAILED");
      const fullQueue: ReceivingQueue = {
        ...firstPage,
        pagination: {
          limit: fullReceipts.size || 1,
          offset: 0,
          has_more: false,
        },
        receipts: [...fullReceipts.values()],
      };

      await saveReceivingQueue(identity, fullQueue);
      setQueue(fullQueue);
      setSelectedReceiptId((current) => (
        current && fullQueue.receipts.some((receipt) => receipt.receipt_id === current) ? current : null
      ));
    } catch (cause) {
      setError(displayError(cause instanceof Error ? cause.message : String(cause)));
    } finally {
      setLoading(false);
    }
  }, [canReceive, identity, online, terminal.deviceSecret, withClient]);

  useEffect(() => {
    if (canReceive && identity) void loadQueue();
  }, [canReceive, identity, loadQueue]);

  function resetAction() {
    setMode("LIST");
    setSelectedReceiptId(null);
    setQuantityDraft({});
    setNote("");
    setReasonCode("");
    setPendingRequestKey(null);
    setError(null);
  }

  function openReceive(receipt: ReceivingQueueReceipt) {
    setSelectedReceiptId(receipt.receipt_id);
    setQuantityDraft({});
    setNote("");
    setPendingRequestKey(null);
    setError(null);
    setMode("RECEIVE");
  }

  function fillRemaining(receipt: ReceivingQueueReceipt) {
    const next: Record<string, string> = {};
    for (const line of receipt.lines) {
      if (line.receiving_ready && Number(line.remaining_stock_qty) > 0) {
        next[line.purchase_line_id] = String(Number(line.remaining_stock_qty));
      }
    }
    setQuantityDraft(next);
    setPendingRequestKey(null);
  }

  function receiveLines(receipt: ReceivingQueueReceipt) {
    return receipt.lines.flatMap((line) => {
      const quantity = asNumber(quantityDraft[line.purchase_line_id] ?? "");
      if (quantity <= 0) return [];
      if (!line.receiving_ready || quantity > Number(line.remaining_stock_qty)) return [];
      return [{ purchase_line_id: line.purchase_line_id, accepted_stock_qty: quantity }];
    });
  }

  function closeLines(receipt: ReceivingQueueReceipt) {
    return receipt.lines.flatMap((line) => {
      const quantity = asNumber(quantityDraft[line.purchase_line_id] ?? "");
      if (quantity <= 0) return [];
      if (!line.receiving_ready || quantity > Number(line.remaining_stock_qty)) return [];
      return [{ purchase_line_id: line.purchase_line_id, close_stock_qty: quantity }];
    });
  }

  function reviewReceive() {
    if (!selectedReceipt || receiveLines(selectedReceipt).length === 0) {
      setError("กรุณากรอกจำนวนที่ตรวจรับจริงอย่างน้อย 1 รายการ");
      return;
    }
    setError(null);
    setPendingRequestKey((current) => current ?? requestKey("pos-purchase-receive"));
    setMode("RECEIVE_REVIEW");
  }

  async function confirmReceive() {
    if (!selectedReceipt || !pendingRequestKey || !online) return;
    const lines = receiveLines(selectedReceipt);
    if (!lines.length) return;
    setSaving(true);
    setError(null);
    try {
      const result = await withClient((client, auth) => client.confirm({
        ...auth,
        receiptId: selectedReceipt.receipt_id,
        lines,
        requestKey: pendingRequestKey,
        note: note.trim() || null,
      }));
      if (!result.ok || !result.data) throw new Error(result.error ?? "POS_STOCK_RECEIVING_FAILED");
      setPendingRequestKey(null);
      setMode("LIST");
      setSelectedReceiptId(null);
      setQuantityDraft({});
      setNote("");
      await loadQueue();
    } catch (cause) {
      setError(displayError(cause instanceof Error ? cause.message : String(cause)));
    } finally {
      setSaving(false);
    }
  }

  async function openClose(receipt: ReceivingQueueReceipt) {
    setSelectedReceiptId(receipt.receipt_id);
    fillRemaining(receipt);
    setNote("");
    setReasonCode("");
    setPendingRequestKey(null);
    setError(null);
    setMode("CLOSE");
    if (reasons.length) return;
    try {
      const result = await withClient((client, auth) => client.closeReasons(auth));
      if (!result.ok || !result.data) throw new Error(result.error ?? "POS_STOCK_RECEIVING_FAILED");
      setReasons(result.data.reasons ?? []);
    } catch (cause) {
      setError(displayError(cause instanceof Error ? cause.message : String(cause)));
    }
  }

  function reviewClose() {
    if (!selectedReceipt || closeLines(selectedReceipt).length === 0) {
      setError("กรุณาระบุจำนวนที่ผู้ขายจะไม่ส่ง");
      return;
    }
    const reason = reasons.find((entry) => entry.reason_code === reasonCode);
    if (!reason) {
      setError("กรุณาเลือกเหตุผลที่ปิดจำนวนค้าง");
      return;
    }
    if (reason.requires_note && !note.trim()) {
      setError("เหตุผลนี้ต้องระบุหมายเหตุ");
      return;
    }
    setError(null);
    setPendingRequestKey((current) => current ?? requestKey("pos-purchase-close-remainder"));
    setMode("CLOSE_REVIEW");
  }

  async function confirmClose() {
    if (!selectedReceipt || !pendingRequestKey || !online) return;
    const lines = closeLines(selectedReceipt);
    if (!lines.length || !reasonCode) return;
    setSaving(true);
    setError(null);
    try {
      const result = await withClient((client, auth) => client.closeRemainder({
        ...auth,
        receiptId: selectedReceipt.receipt_id,
        lines,
        reasonCode,
        requestKey: pendingRequestKey,
        note: note.trim() || null,
      }));
      if (!result.ok || !result.data) throw new Error(result.error ?? "POS_STOCK_RECEIVING_FAILED");
      setPendingRequestKey(null);
      setMode("LIST");
      setSelectedReceiptId(null);
      setQuantityDraft({});
      setNote("");
      setReasonCode("");
      await loadQueue();
    } catch (cause) {
      setError(displayError(cause instanceof Error ? cause.message : String(cause)));
    } finally {
      setSaving(false);
    }
  }

  if (!canReceive || !identity) return null;

  return (
    <>
      <button
        className="receiving-launch"
        type="button"
        disabled={disabled}
        onClick={() => { setOpen(true); void loadQueue(); }}
      >
        <span aria-hidden="true">▣</span>
        รับสินค้า
        {pendingCount > 0 ? <b>{pendingCount}</b> : null}
      </button>

      {open ? (
        <div className="receiving-backdrop" role="presentation">
          <section className="receiving-panel" role="dialog" aria-modal="true" aria-labelledby="receiving-title">
            <header className="receiving-panel__header">
              <div>
                <p>MORE STOCK · PURCHASE RECEIVING</p>
                <h2 id="receiving-title">รับสินค้า</h2>
                <span>{actor.branch_name} · {actor.employee_name}</span>
              </div>
              <button type="button" aria-label="ปิดหน้ารับสินค้า" onClick={() => { setOpen(false); resetAction(); }}>×</button>
            </header>

            {!online ? <div className="receiving-alert">Offline: อ่านคิวรับสินค้าล่าสุดจากเครื่องได้ แต่การยืนยันรับสินค้าต้อง Online เพื่อให้ Stock Core เป็น Final Authority</div> : null}
            {error ? <div className="receiving-error">{error}</div> : null}

            {mode === "LIST" ? (
              <div className="receiving-content">
                <div className="receiving-tabs" role="tablist" aria-label="สถานะการรับสินค้า">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={view === "OPEN"}
                    className={view === "OPEN" ? "is-active" : ""}
                    onClick={() => setView("OPEN")}
                  >
                    รอรับ <b>{pendingCount}</b>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={view === "RESOLVED"}
                    className={view === "RESOLVED" ? "is-active" : ""}
                    onClick={() => setView("RESOLVED")}
                  >
                    รับแล้ว <b>{resolvedCount}</b>
                  </button>
                </div>

                <div className="receiving-summary">
                  {view === "OPEN" ? (
                    <>
                      <div><span>รอรับ</span><strong>{queue?.summary.pending_receipt_count ?? 0}</strong></div>
                      <div><span>รับแล้วบางส่วน</span><strong>{queue?.summary.partial_receipt_count ?? 0}</strong></div>
                      <div><span>ต้องตั้งค่าก่อน</span><strong>{queue?.summary.needs_setup_receipt_count ?? 0}</strong></div>
                    </>
                  ) : (
                    <>
                      <div><span>รับครบแล้ว</span><strong>{queue?.summary.received_receipt_count ?? 0}</strong></div>
                      <div><span>ปิดยอดค้างแล้ว</span><strong>{queue?.summary.closed_with_shortage_receipt_count ?? 0}</strong></div>
                      <div><span>รวมรับแล้ว</span><strong>{resolvedCount}</strong></div>
                    </>
                  )}
                  <button type="button" disabled={!online || loading} onClick={() => void loadQueue()}>{loading ? "กำลังรีเฟรช…" : "รีเฟรช"}</button>
                </div>

                {visibleReceipts.length ? (
                  <div className="receiving-list">
                    {visibleReceipts.map((receipt) => (
                      <article className="receiving-receipt" key={receipt.receipt_id}>
                        <div className="receiving-receipt__head">
                          <div>
                            <span className={`receiving-state receiving-state--${receipt.receiving_status.toLowerCase()}`}>{receiptStatusLabel(receipt)}</span>
                            <small>{formatDate(receipt.purchase_date)}</small>
                            <h3>{receipt.supplier_name || "ไม่ระบุผู้ขาย"}</h3>
                            <p>{receipt.internal_bill_no || receipt.document_ref || receipt.receipt_id}</p>
                          </div>
                          <div className="receiving-receipt__numbers">
                            <span>{view === "OPEN" ? "ค้างรับ" : "รับแล้ว"}</span>
                            <strong>{Number(view === "OPEN" ? receipt.remaining_stock_qty : receipt.received_stock_qty)}</strong>
                            <small>หน่วยรวม</small>
                          </div>
                        </div>
                        <div className="receiving-mini-lines">
                          {receipt.lines.map((line) => (
                            <div key={line.purchase_line_id}>
                              <span><strong>{line.sku_name}</strong><small>{line.sku_code}</small></span>
                              <span>สั่ง <b>{Number(line.ordered_stock_qty)}</b></span>
                              <span>รับแล้ว <b>{Number(line.received_stock_qty)}</b></span>
                              <span>ค้าง <b>{Number(line.remaining_stock_qty)}</b> {line.stock_unit_name}</span>
                            </div>
                          ))}
                        </div>
                        {view === "OPEN" ? (
                          <div className="receiving-receipt__actions">
                            <button className="receiving-primary" type="button" disabled={!online || receipt.receiving_status === "NEEDS_SETUP"} onClick={() => openReceive(receipt)}>ตรวจรับสินค้า</button>
                            <button className="receiving-secondary" type="button" disabled={!online || receipt.receiving_status === "NEEDS_SETUP"} onClick={() => void openClose(receipt)}>ปิดจำนวนที่ไม่ส่ง</button>
                          </div>
                        ) : null}
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="receiving-empty">
                    <strong>{loading ? "กำลังโหลดรายการรับสินค้า…" : view === "OPEN" ? "ไม่มีบิลรอรับสินค้า" : "ยังไม่มีประวัติรับสินค้า"}</strong>
                    <p>{view === "OPEN" ? "บิลซื้อใหม่หลังเปิดระบบจะเข้าคิวจาก Purchase Core โดยอัตโนมัติ" : "รายการที่รับครบหรือปิดยอดค้างแล้วจะแสดงที่นี่"}</p>
                  </div>
                )}
              </div>
            ) : selectedReceipt ? (
              <div className="receiving-content receiving-action-view">
                <button className="receiving-back" type="button" disabled={saving} onClick={() => { setMode("LIST"); setPendingRequestKey(null); setError(null); }}>← กลับคิวรับสินค้า</button>
                <div className="receiving-action-title">
                  <div><small>{selectedReceipt.internal_bill_no}</small><h3>{selectedReceipt.supplier_name || "ไม่ระบุผู้ขาย"}</h3></div>
                  <span>{mode.startsWith("CLOSE") ? "ปิดจำนวนค้างที่ไม่ส่ง" : "กรอกจำนวนที่รับจริง"}</span>
                </div>

                {(mode === "RECEIVE" || mode === "CLOSE") ? (
                  <>
                    <div className="receiving-edit-lines">
                      {selectedReceipt.lines.map((line) => (
                        <label key={line.purchase_line_id} className={!line.receiving_ready ? "receiving-line-disabled" : ""}>
                          <span><strong>{line.sku_name}</strong><small>{line.sku_code} · สั่ง {Number(line.ordered_stock_qty)} · รับแล้ว {Number(line.received_stock_qty)} · ค้าง {Number(line.remaining_stock_qty)} {line.stock_unit_name}</small></span>
                          <input
                            type="number"
                            min="0"
                            max={Number(line.remaining_stock_qty)}
                            step="any"
                            inputMode="decimal"
                            disabled={!line.receiving_ready}
                            value={quantityDraft[line.purchase_line_id] ?? ""}
                            placeholder="0"
                            onChange={(event) => { setQuantityDraft({ ...quantityDraft, [line.purchase_line_id]: event.target.value }); setPendingRequestKey(null); }}
                          />
                        </label>
                      ))}
                    </div>
                    <button className="receiving-fill" type="button" onClick={() => fillRemaining(selectedReceipt)}>ใส่จำนวนค้างทั้งหมด</button>

                    {mode === "CLOSE" ? (
                      <label className="receiving-field"><span>เหตุผลที่ไม่รับส่วนที่เหลือ</span><select value={reasonCode} onChange={(event) => { setReasonCode(event.target.value); setPendingRequestKey(null); }}><option value="">เลือกเหตุผล</option>{reasons.map((reason) => <option key={reason.reason_code} value={reason.reason_code}>{reason.label_th}</option>)}</select></label>
                    ) : null}
                    <label className="receiving-field"><span>หมายเหตุ (ถ้ามี)</span><textarea value={note} onChange={(event) => { setNote(event.target.value); setPendingRequestKey(null); }} rows={2} /></label>
                    <button className="receiving-primary receiving-review" type="button" onClick={mode === "RECEIVE" ? reviewReceive : reviewClose}>ตรวจทานก่อนยืนยัน</button>
                  </>
                ) : (
                  <div className="receiving-review-box">
                    <p className="receiving-review-warning">กรุณาตรวจจำนวนกับสินค้าที่อยู่ตรงหน้าก่อนยืนยัน การรับสินค้าจะสร้าง Stock Movement จริง</p>
                    {(mode === "RECEIVE_REVIEW" ? receiveLines(selectedReceipt) : closeLines(selectedReceipt)).map((entry) => {
                      const line = selectedReceipt.lines.find((candidate) => candidate.purchase_line_id === entry.purchase_line_id);
                      const quantity = "accepted_stock_qty" in entry ? entry.accepted_stock_qty : entry.close_stock_qty;
                      return <div key={entry.purchase_line_id}><span>{line?.sku_name}</span><strong>{quantity} {line?.stock_unit_name}</strong></div>;
                    })}
                    {mode === "CLOSE_REVIEW" ? <div><span>เหตุผล</span><strong>{reasons.find((reason) => reason.reason_code === reasonCode)?.label_th}</strong></div> : null}
                    {note.trim() ? <div><span>หมายเหตุ</span><strong>{note.trim()}</strong></div> : null}
                    <div className="receiving-review-actions">
                      <button className="receiving-secondary" type="button" disabled={saving} onClick={() => setMode(mode === "RECEIVE_REVIEW" ? "RECEIVE" : "CLOSE")}>กลับไปแก้</button>
                      <button className="receiving-confirm" type="button" disabled={saving || !online} onClick={() => void (mode === "RECEIVE_REVIEW" ? confirmReceive() : confirmClose())}>{saving ? "กำลังยืนยัน…" : mode === "RECEIVE_REVIEW" ? "ยืนยันรับสินค้า" : "ยืนยันปิดจำนวนค้าง"}</button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </section>
        </div>
      ) : null}
    </>
  );
}
